import React, {useCallback, useEffect, useRef, useState} from "react";
import {useKeyboardShortcut} from "../UseKeyboardShortcut";
import loadingSpinner from "../../assets/loading.svg";
import './endless-scroll-img.css'

const ZOOM_STEP = 1.25;
const MAX_ABSOLUTE_SCALE = 4;
const BOX_SCREEN_WIDTH = 3;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// The view that is shown first and what "Reset" returns to. Wide images are
// fitted to the width and centred on the defect, close-ups are fitted whole.
function homeView(mode, natural, box, viewport) {
    if (mode === 'close') {
        const scale = Math.min(viewport.width / natural.w, viewport.height / natural.h);
        return {
            scale,
            x: (viewport.width - natural.w * scale) / 2,
            y: (viewport.height - natural.h * scale) / 2,
        };
    }

    const scale = viewport.width / natural.w;
    const height = natural.h * scale;
    if (height <= viewport.height) return {scale, x: 0, y: (viewport.height - height) / 2};

    const boxCenterY = box ? (box.y + box.height / 2) * scale : height / 2;
    return {scale, x: 0, y: clamp(viewport.height / 2 - boxCenterY, viewport.height - height, 0)};
}

export default function ({wideImg, closeUpImg, box, boxLoading, extras = [], photoIndex = 0, onPhotoChange}) {
    const [mode, setMode] = useState('wide');
    const [showBox, setShowBox] = useState(true);
    const [natural, setNatural] = useState(null);
    const [view, setView] = useState({scale: 1, x: 0, y: 0});
    const [home, setHome] = useState({scale: 1, x: 0, y: 0});
    const [dragging, setDragging] = useState(false);

    const viewportRef = useRef(null);

    // Photo 0 is the defect itself; 1..n are its extra angles (opposite pass of the same blade).
    const photoCount = 1 + extras.length;
    const photo = Math.min(photoIndex, photoCount - 1);
    const extra = photo > 0 ? extras[photo - 1] : null;
    const wideSrc = extra ? extra.large_url : wideImg;
    const closeSrc = extra ? (extra.small_url || extra.large_url) : closeUpImg;
    const src = mode === 'wide' ? wideSrc : closeSrc;
    const isLoading = !src || src === loadingSpinner;

    useEffect(() => {
        setNatural(null);
    }, [src]);

    const resetView = useCallback(() => {
        const viewport = viewportRef.current;
        if (!natural || !viewport) return;
        const next = homeView(mode, natural, box, {width: viewport.clientWidth, height: viewport.clientHeight});
        setHome(next);
        setView(next);
    }, [mode, natural, box]);

    useEffect(resetView, [resetView]);

    const zoomAt = useCallback((factor, cx, cy) => {
        setView(current => {
            const scale = clamp(current.scale * factor, home.scale * 0.5, Math.max(MAX_ABSOLUTE_SCALE, home.scale * 4));
            const ratio = scale / current.scale;
            return {scale, x: cx - (cx - current.x) * ratio, y: cy - (cy - current.y) * ratio};
        });
    }, [home.scale]);

    const zoomFromCenter = useCallback(factor => {
        const viewport = viewportRef.current;
        if (viewport) zoomAt(factor, viewport.clientWidth / 2, viewport.clientHeight / 2);
    }, [zoomAt]);

    // Native listener: React's onWheel is passive, so it cannot stop the page
    // behind the popup from scrolling while zooming.
    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;
        const onWheel = event => {
            event.preventDefault();
            const {left, top} = viewport.getBoundingClientRect();
            zoomAt(Math.exp(-event.deltaY / 500), event.clientX - left, event.clientY - top);
        };
        viewport.addEventListener('wheel', onWheel, {passive: false});
        return () => viewport.removeEventListener('wheel', onWheel);
    }, [zoomAt]);

    const changePhoto = step => onPhotoChange && photoCount > 1 &&
        onPhotoChange((photo + step + photoCount) % photoCount);
    useKeyboardShortcut(['ArrowLeft'], () => {}, () => changePhoto(-1));
    useKeyboardShortcut(['ArrowRight'], () => {}, () => changePhoto(1));

    // The box was drawn on the defect's own wide image, so it does not fit the extra angles.
    const boxAvailable = mode === 'wide' && !!box && photo === 0;
    const toggleBox = () => boxAvailable && setShowBox(shown => !shown);

    useKeyboardShortcut(['z'], () => {}, () => zoomFromCenter(1 / ZOOM_STEP));
    useKeyboardShortcut(['x'], () => {}, () => zoomFromCenter(ZOOM_STEP));
    useKeyboardShortcut(['b'], () => {}, toggleBox);

    function onPointerDown(event) {
        event.currentTarget.setPointerCapture(event.pointerId);
        setDragging(true);
    }

    function onPointerMove(event) {
        if (!dragging) return;
        setView(current => ({...current, x: current.x + event.movementX, y: current.y + event.movementY}));
    }

    function onPointerUp(event) {
        event.currentTarget.releasePointerCapture(event.pointerId);
        setDragging(false);
    }

    const boxWidth = BOX_SCREEN_WIDTH / view.scale;

    return <div className='image-viewer'>
        <div className='image-toolbar'>
            {photoCount > 1 && <div className='toolbar-group photo-switcher'>
                <button onClick={() => changePhoto(-1)} title='Previous photo (Left arrow)'>&lsaquo;</button>
                <span className='photo-label'>
                    {photo === 0 ? 'Original' : `Opposite pass ${photo}`}
                    <span className='photo-count'> ({photo + 1}/{photoCount})</span>
                    {extra && extra.comment && <span className='photo-comment'> · {extra.comment}</span>}
                </span>
                <button onClick={() => changePhoto(1)} title='Next photo (Right arrow)'>&rsaquo;</button>
            </div>}
            <div className='toolbar-group'>
                <button className={mode === 'wide' ? 'active' : ''} onClick={() => setMode('wide')}>Wide</button>
                <button className={mode === 'close' ? 'active' : ''} onClick={() => setMode('close')}>Close-up</button>
            </div>
            <div className='toolbar-group'>
                <button onClick={() => zoomFromCenter(1 / ZOOM_STEP)} title='Zoom out (Z)'>&minus;</button>
                <span className='zoom-level'>{Math.round(view.scale / home.scale * 100)}%</span>
                <button onClick={() => zoomFromCenter(ZOOM_STEP)} title='Zoom in (X)'>+</button>
                <button onClick={resetView} title='Reset size and position'>Reset</button>
            </div>
            <div className='toolbar-group'>
                <button
                    className={boxAvailable && showBox ? 'active' : ''}
                    disabled={!boxAvailable}
                    onClick={toggleBox}
                    title={photo > 0 ? 'The box only applies to the original photo' :
                        mode === 'close' ? 'The box only applies to the wide image' :
                        boxLoading ? 'Locating the defect box' :
                            box ? 'Show or hide the defect box (B)' : 'No defect box available for this defect'}>
                    {boxLoading && mode === 'wide' ? 'Locating box...' : 'Defect box'}
                </button>
            </div>
        </div>
        <div
            className={`image-viewport${dragging ? ' dragging' : ''}`}
            ref={viewportRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}>
            {isLoading && <img className='image-loading' src={loadingSpinner} alt='loading'/>}
            {!isLoading && <div
                className='image-content'
                style={{transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`}}>
                <img
                    key={src}
                    src={src}
                    draggable={false}
                    alt={mode === 'wide' ? 'wide defect' : 'defect close-up'}
                    onLoad={e => setNatural({w: e.target.naturalWidth, h: e.target.naturalHeight})}
                    style={natural ? {width: natural.w, height: natural.h} : {visibility: 'hidden'}}/>
                {natural && boxAvailable && showBox && <div
                    className='defect-box'
                    style={{
                        left: box.x,
                        top: box.y,
                        width: box.width,
                        height: box.height,
                        borderWidth: boxWidth,
                    }}/>}
            </div>}
        </div>
    </div>
}
